// The MTD submit endpoint returns advisory warnings in the same error
// collection it uses for real failures, on both successful (HTTP 200) and failed
// responses -- but the two responses use DIFFERENT envelopes.
//
// Failure (an error DTO: the collection is flat, and the real failure can sit in the
// top-level `message` while the collection holds only an advisory):
//
//   {
//     "errors":  [{ "code": "HMRC_AUTH_EXPIRING", "message": "HMRC authorization expires on ..." }],
//     "code":    null,
//     "message": "Could not find an open obligation for this period"
//   }
//
// Success (the MTD response model, where `MTDErrors` wraps its own `Errors` list, so
// the collection is nested one level deeper):
//
//   { ..., "errors": { "errors": [{ "code": "HMRC_AUTH_EXPIRING", "message": "..." }] } }
//
// Both are handled, under `response` as well, and key lookup is case-insensitive so
// PascalCase serialization of the .NET models works too. Two consequences worth
// keeping in mind:
//   - reading `errors[0]` blindly shows the advisory in place of the actual error,
//     which is the defect this module exists to prevent;
//   - handling only the flat shape silently drops the advisory on success, because
//     `errors` is then an object rather than an array.

export const MTD_SUBMIT_FILING_LOG_SCOPE = 'MTD_SUBMIT_FILING';

// Codes that are advisory only: the submission is not rejected because of them, so
// they belong in a warning banner rather than an error banner.
export const MTD_WARNING_CODES = ['HMRC_AUTH_EXPIRING'];

const asTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');

// Case-insensitive property read, so camelCase and PascalCase bodies behave alike.
const readKey = (obj, name) => {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }
  const key = Object.keys(obj).find((candidate) => candidate.toLowerCase() === name);
  return key === undefined ? undefined : obj[key];
};

// Finds the error collection across the envelopes described above: flat `errors`,
// nested `errors.errors`, and either of those under `response`. The first candidate
// that is actually an array wins.
const resolveEntries = (body) => {
  if (Array.isArray(body)) {
    return body;
  }
  const errors = readKey(body, 'errors');
  const response = readKey(body, 'response');
  const responseErrors = readKey(response, 'errors');
  const candidates = [errors, readKey(errors, 'errors'), responseErrors, readKey(responseErrors, 'errors')];
  return candidates.find(Array.isArray) || [];
};

const entryMessage = (entry) =>
  typeof entry === 'string' ? entry.trim() : asTrimmedString(readKey(entry, 'message'));

// An entry only counts as advisory when its code is explicitly recognised. Anything
// else -- including an entry with no code at all -- is treated as a real error, so an
// unrecognised advisory code surfaces as a problem instead of being swallowed.
const isWarningEntry = (entry) =>
  !!entry && MTD_WARNING_CODES.indexOf(asTrimmedString(readKey(entry, 'code'))) >= 0;

/**
 * Splits an MTD submit response body into its advisory warning text and its real
 * error text. Pure, and never throws.
 *
 * @returns {{warning: string, error: string, errorCode: (string|undefined)}}
 *   warning   deduped messages of entries whose code is in MTD_WARNING_CODES,
 *             joined with a space (each message is a complete sentence)
 *   error     the first non-warning entry's message, else the top-level `message`
 *   errorCode that entry's code, else the top-level `code`, else undefined
 */
export function classifyMtdSubmissionResponse(body) {
  const empty = { warning: '', error: '', errorCode: undefined };
  try {
    if (!body || typeof body !== 'object') {
      return empty;
    }

    const entries = resolveEntries(body);
    const warnings = [];
    let firstError;

    entries.forEach((entry) => {
      const message = entryMessage(entry);
      if (isWarningEntry(entry)) {
        if (message && warnings.indexOf(message) < 0) {
          warnings.push(message);
        }
        return;
      }
      if (!firstError && message) {
        firstError = { message, code: asTrimmedString(readKey(entry, 'code')) };
      }
    });

    const topLevelMessage = asTrimmedString(readKey(body, 'message'));
    const topLevelCode = asTrimmedString(readKey(body, 'code'));

    return {
      warning: warnings.join(' '),
      error: firstError ? firstError.message : topLevelMessage,
      errorCode: (firstError && firstError.code) || topLevelCode || undefined
    };
  } catch (e) {
    return empty;
  }
}
